import {
    ACTION_TYPES,
    ITEM_LOCATION_TYPES,
    ROADMAP_TYPES,
    SPECIAL_ACTION_IDS,
} from '@/roadmaps/constants/roadmaps.constants';
import {
    RoadmapForbiddenInteractionError,
    RoadmapOutOfBoundsInteractionError,
} from '@/roadmaps/errors/roadmaps-errors';
import { getPendingDragActions as getPendingDragActionsColumns } from '@/roadmaps/helpers/interactions/roadmaps-column-interactions.helper';
import { getPendingDragActions as getPendingDragActionsTimeline } from '@/roadmaps/helpers/interactions/roadmaps-timeline-interactions.helper';
import { useDraggable } from '@/roadmaps/use/use-draggable';
import {
    calculateAllPendingSwimlaneRowHeights,
    recalculateRowHeightAfterExpandCollapse,
} from '@/roadmaps/use/use-swimlanes';
/* eslint-disable internal-rules/no-store-in-composables */
/* eslint-disable no-restricted-imports */
import store from '@/vuex/store';
import each from 'lodash/each';
import find from 'lodash/find';
import map from 'lodash/map';
import omitBy from 'lodash/omitBy';
import pickBy from 'lodash/pickBy';
import reduce from 'lodash/reduce';
import some from 'lodash/some';
import Vue, { ref, unref } from 'vue';

let pendingActions;
let previewTimeout;
let temporaryExpandedItemWhileNesting;

export function initUsePendingActions () {
    pendingActions = ref({});
    const items = store.getters['roadmaps/items/items'];
    each(items, (item) => {
        setPendingActionNone(item.id);
    });
    setPendingActionNone(SPECIAL_ACTION_IDS.PLACEHOLDER_ITEM);
    setPendingActionNone(SPECIAL_ACTION_IDS.PROMOTE_PARENT);
}

export function getAllPendingActions () {
    return unref(pendingActions);
}

export function getPendingActionById (id) {
    if (!unref(pendingActions)[id]) setPendingActionNone(id);

    return unref(pendingActions)[id];
}

export function getFirstPendingActionByType (type) {
    return find(unref(pendingActions), (action) => action.type === type);
}

export function getAllPendingActionsByType (type) {
    return pickBy(unref(pendingActions), (action) => action.type === type);
}

export function getInteractingItemPendingAction () {
    const interactingItemActionTypes = [
        ACTION_TYPES.DRAG,
        ACTION_TYPES.RESIZE_LEFT,
        ACTION_TYPES.RESIZE_RIGHT,
        ACTION_TYPES.NEST,
        ACTION_TYPES.PROMOTE_CHILD_SOURCE,
        ACTION_TYPES.FORBIDDEN,
        ACTION_TYPES.OUT_OF_BOUNDS,
    ];

    return find(unref(pendingActions), (action) => interactingItemActionTypes.includes(action.type));
}

export function setPendingAction (id, action) {
    Vue.set(unref(pendingActions), id, action);
}

export function setPendingActionNone (id) {
    setPendingAction(id, {
        id,
        type: ACTION_TYPES.NONE,
        updates: {},
        targetItem: null,
        preview: false,
    });
}

export function clearPendingActions () {
    cancelShowPreview();
    temporaryExpandedItemWhileNesting = null;
    each(unref(pendingActions), (action, id) => {
        if (action.type !== ACTION_TYPES.NONE) {
            setPendingActionNone(id);
        }
    });
}

export function setInitialPendingAction (source, type) {
    const targetItem = type === ACTION_TYPES.NEST ? store.getters['roadmaps/items/itemById'](source.parentId) : null;
    setPendingAction(source.id, {
        id: source.id,
        type,
        updates: {},
        targetItem,
        preview: false,
    });
}

export function applyNewPendingActions (newPendingActions) {
    const wasNesting = some(unref(pendingActions), { type: ACTION_TYPES.NEST });
    const nowNesting = some(newPendingActions, { type: ACTION_TYPES.NEST });
    const previousNestTarget = getFirstPendingActionByType(ACTION_TYPES.NEST)?.targetItem;

    each(unref(pendingActions), (__, id) => {
        if (hasPendingActionChanged(id, newPendingActions[id])) {
            if (!newPendingActions[id]) {
                setPendingActionNone(id);
            } else {
                setPendingAction(id, newPendingActions[id]);
            }
        }
    });

    const hasStoppedNesting = wasNesting && !nowNesting;
    // REMOVE in part 2 APP-87824 or APP-86899
    const isTimelineRoadmap = store.getters['roadmaps/roadmaps/isTimelineRoadmap'];
    if (isTimelineRoadmap && hasStoppedNesting) stoppedNesting(previousNestTarget);
}

function hasPendingActionChanged (id, newPendingAction) {
    if (!newPendingAction) {
        const wasPendingActionRemoved = !newPendingAction && unref(pendingActions)[id].type !== ACTION_TYPES.NONE;

        return wasPendingActionRemoved;
    }

    return (
        newPendingAction.type !== unref(pendingActions)[id].type ||
        JSON.stringify(newPendingAction.updates) !== JSON.stringify(unref(pendingActions)[id].updates) ||
        newPendingAction.targetItem !== unref(pendingActions)[id].targetItem
    );
}

function stoppedNesting (previousNestTarget) {
    const children = store.getters['roadmaps/items/childItemsWithoutDraggedItem'](previousNestTarget.id);
    const wasLastChild = children.length === 0;

    if (temporaryExpandedItemWhileNesting || wasLastChild) {
        store.dispatch('roadmaps/items/setIsExpanded', {
            id: previousNestTarget.id,
            expanded: false,
        });
        temporaryExpandedItemWhileNesting = null;
    }
}

export function showPreviews () {
    cancelShowPreview();

    // Cypress doesn't need a delay for previews
    const previewDelay = window.Cypress ? 0 : 250;
    previewTimeout = setTimeout(() => {
        each(unref(pendingActions), (action, id) => {
            if (action.type !== ACTION_TYPES.NONE && !unref(pendingActions)[id].preview) {
                pendingActions.value[id].preview = true;
            }
            if (action.type === ACTION_TYPES.NEST && !action.targetItem.isExpanded) {
                store.dispatch('roadmaps/items/setIsExpanded', { id: action.targetItem.id, expanded: true });
                temporaryExpandedItemWhileNesting = action.targetItem;
                if (store.getters['roadmaps/roadmaps/isTimelineRoadmap']) {
                    recalculateRowHeightAfterExpandCollapse(action.targetItem.parentId, action.targetItem.row);
                }
            }
        });
        if (store.getters['roadmaps/roadmaps/isTimelineRoadmap']) {
            calculateAllPendingSwimlaneRowHeights();
        }
    }, previewDelay);
}

function cancelShowPreview () {
    if (previewTimeout) clearTimeout(previewTimeout);
}

export async function commitPendingActions (rootSource) {
    const { setDraggedItem } = useDraggable();

    if (unref(pendingActions)[rootSource.id].type === ACTION_TYPES.FORBIDDEN) {
        setDraggedItem(null);
        clearPendingActions();
        throw new RoadmapForbiddenInteractionError();
    } else if (unref(pendingActions)[rootSource.id].type === ACTION_TYPES.OUT_OF_BOUNDS) {
        setDraggedItem(null);
        clearPendingActions();
        throw new RoadmapOutOfBoundsInteractionError();
    } else if (
        rootSource.onBacklog &&
        rootSource.type === ITEM_LOCATION_TYPES.IDEA.key &&
        unref(pendingActions)[rootSource.id].type !== ACTION_TYPES.PROMOTE_CHILD_SOURCE
    ) {
        await commitPendingIdeaActions(rootSource);
    } else if (
        rootSource.onBacklog &&
        rootSource.parentType !== ITEM_LOCATION_TYPES.BACKLOG.key &&
        unref(pendingActions)[rootSource.id].type === ACTION_TYPES.DRAG
    ) {
        showBacklogUnlinkModal(rootSource);
    } else if (unref(pendingActions)[rootSource.id].type === ACTION_TYPES.PROMOTE_CHILD_SOURCE) {
        commitPendingPromoteActions(rootSource);
    } else {
        await commitPendingLocationUpdates();
    }
}

async function commitPendingLocationUpdates () {
    const locationUpdates = omitBy(unref(pendingActions), (action) => action.type === ACTION_TYPES.NONE);
    const { setDraggedItem } = useDraggable();
    setDraggedItem(null);
    clearPendingActions();
    if (Object.keys(locationUpdates).length > 0) {
        const itemsToUpdate = getItemsWithUpdates(locationUpdates);
        await store.dispatch('roadmaps/items/updateLocations', itemsToUpdate);
    }
}

async function commitPendingIdeaActions (rootSource) {
    const pendingResolves = getAllPendingActionsByType(ACTION_TYPES.RESOLVE);
    const resolvesWithUpdates = getItemsWithUpdates(pendingResolves);
    const ideaToCreateOnRoadmap = Object.assign({}, rootSource, unref(pendingActions)[rootSource.id].updates);

    const { setDraggedItem } = useDraggable();
    setDraggedItem(null);
    clearPendingActions();

    await store.dispatch('roadmaps/items/createIdeaOnRoadmap', {
        ideaToCreateOnRoadmap,
        resolvingItems: resolvesWithUpdates,
    });
}

function commitPendingPromoteActions (rootSource) {
    const promoteParentUpdates = getFirstPendingActionByType(ACTION_TYPES.PROMOTE_PARENT).updates;

    const sourcePendingAction = unref(pendingActions)[rootSource.id];

    const sourceWithUpdates = Object.assign({}, rootSource, sourcePendingAction.updates);

    const target = sourcePendingAction.targetItem;
    const targetWithUpdates = Object.assign({}, target, unref(pendingActions)[target.id].updates);

    let importedItem;
    const children = [];
    const isSourceImportedIdea = rootSource.id.startsWith(SPECIAL_ACTION_IDS.ADD_IDEA);
    const isTargetImportedIdea = target.id.startsWith(SPECIAL_ACTION_IDS.ADD_IDEA);
    if (isSourceImportedIdea) {
        importedItem = sourceWithUpdates;
        children.push(targetWithUpdates);
    } else if (isTargetImportedIdea) {
        importedItem = targetWithUpdates;
        children.push(sourceWithUpdates);
    } else {
        children.push(targetWithUpdates, sourceWithUpdates);
    }

    const pendingResolves = getAllPendingActionsByType(ACTION_TYPES.RESOLVE);
    const resolvesWithUpdates = map(pendingResolves, (__, id) => {
        const resolvingItem = store.getters['roadmaps/items/itemById'](id);

        return Object.assign({}, resolvingItem, unref(pendingActions)[resolvingItem.id].updates);
    });

    const { setDraggedItem } = useDraggable();
    setDraggedItem(null);
    clearPendingActions();

    store.dispatch('roadmaps/modals/openItemPromoteModal', {
        promoteParentUpdates,
        children,
        importedItem,
        resolvingItems: resolvesWithUpdates,
    });
}

function showBacklogUnlinkModal (rootSource) {
    const itemsToUpdateIfUnlinking = getItemsToUpdateIfUnlinking(rootSource);
    const parent = store.getters['roadmaps/items/itemById'](rootSource.parentId);
    const pendingUpdates = unref(pendingActions)[rootSource.id].updates;
    const itemsToUpdateIfAddingGroup = getItemsToUpdateIfAddingGroup(parent, pendingUpdates);
    store.dispatch('roadmaps/modals/openUnlinkBacklogItemModal', {
        source: rootSource,
        parent,
        itemsToUpdateIfUnlinking,
        itemsToUpdateIfAddingGroup,
    });
}

function getItemsToUpdateIfUnlinking (source) {
    const sourceWithUpdates = Object.assign({}, source, getPendingActionById(source.id).updates);

    const pendingResolves = getAllPendingActionsByType(ACTION_TYPES.RESOLVE);
    const resolvingItemsWithUpdates = getItemsWithUpdates(pendingResolves);

    return [sourceWithUpdates, ...resolvingItemsWithUpdates];
}

function getItemsToUpdateIfAddingGroup (newSource, newSourceUpdates) {
    let pendingActionsIfAddingGroup;
    switch (store.getters['roadmaps/roadmaps/currentRoadmap'].type) {
        case ROADMAP_TYPES.TIMELINE:
            pendingActionsIfAddingGroup = getPendingDragActionsTimeline(newSource, newSourceUpdates);
            break;
        case ROADMAP_TYPES.COLUMN:
            pendingActionsIfAddingGroup = getPendingDragActionsColumns(newSource, newSourceUpdates);
            break;
    }

    return getItemsWithUpdates(pendingActionsIfAddingGroup);
}

export function getItemsWithUpdates (actions) {
    return reduce(
        actions,
        (accumulator, action) => {
            const item = store.getters['roadmaps/items/itemById'](action.id);
            accumulator.push(Object.assign({}, item, action.updates));

            return accumulator;
        },
        [],
    );
}
